import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, Plus, Trash2, Upload } from 'lucide-react';
import { EditableText } from '../EditableText';
import { EditableImage } from '../EditableImage';
import aboutHeroImage from "@/assets/about-hero.jpg";
import resultsLivingRoom from "@/assets/results-living-room.jpg";
import resultsKitchen from "@/assets/results-kitchen.jpg";
import resultsBathroom from "@/assets/results-bathroom.jpg";
import { getIconComponent, ICON_NAMES } from "@/components/admin/icon-map";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { toast } from 'sonner';
import { mergeTeamMembersIntoAbout, useOrgTeamMembers, type AboutContent } from '@/hooks/useAboutContent';

const DEFAULT_CONTENT = {
  hero: {
    title: 'Making Cardiff and Newport homes sparkle',
    subtitle: 'We started The Polished Version with a simple mission to provide reliable, high quality cleaning services people can genuinely trust.',
    imageUrl: '',
    features: ['Online scheduling', 'Transparent pricing', 'Fast response times'],
  },
  valuesTitle: 'What We Stand For',
  values: [
    { icon: 'Heart', title: 'Passion', description: "We genuinely love what we do. Every clean is an opportunity to make someone's day better." },
    { icon: 'Shield', title: 'Trust', description: 'Your home is your sanctuary. We treat it with the respect and care it deserves.' },
    { icon: 'Users', title: 'Community', description: 'We employ local cleaners and are proud to serve our community.' },
    { icon: 'Award', title: 'Excellence', description: 'We never cut corners. Every surface, every room – nothing less than perfect.' },
  ],
  teamTitle: 'Meet Our Team',
  team: [
    { name: 'Nick Patel', role: 'Founder and Managing Director', bio: 'With over 10 years in multi industry operational management, Nick brings a practical, people focused approach.', avatar: '', initials: 'NP' },
    { name: 'Chris Thomas', role: 'Founder and AI Architect', bio: 'With a background in financial services and entrepreneurship, Chris brings strategic thinking and creativity.', avatar: '', initials: 'CT' },
    { name: 'Kirstie Harries', role: 'Customer Experience Lead', bio: 'Kirstie is the first point of contact for our clients, using modern tools to streamline communication.', avatar: '', initials: 'KH' },
  ],
  results: {
    title: 'See Our Results',
    subtitle: '',
    images: [
      { url: '', alt: 'Spotless living room after cleaning' },
      { url: '', alt: 'Sparkling clean kitchen' },
      { url: '', alt: 'Fresh and clean bathroom' },
    ],
  },
  cta: { title: 'Join the many households and businesses choosing a cleaner, easier way', subtitle: 'Join over 500 happy customers across Cardiff & Newport', buttonText: 'Book my cleaning' },
};

// Icon Picker Component
function IconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const [open, setOpen] = useState(false);
  const Icon = getIconComponent(value);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4 hover:ring-2 hover:ring-primary transition-all cursor-pointer">
          <Icon className="w-7 h-7 text-primary" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="center">
        <div className="grid grid-cols-6 gap-1">
          {ICON_NAMES.map((iconName) => {
            const IconOption = getIconComponent(iconName);
            return (
              <button
                key={iconName}
                onClick={() => {
                  onChange(iconName);
                  setOpen(false);
                }}
                className={cn(
                  "p-2 rounded hover:bg-muted",
                  value === iconName && "bg-primary/10 text-primary"
                )}
                title={iconName}
              >
                <IconOption className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Team Avatar Component with upload
function TeamAvatar({ 
  avatar, 
  initials, 
  name,
  onAvatarChange 
}: { 
  avatar?: string; 
  initials: string; 
  name: string;
  onAvatarChange: (url: string) => void;
}) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [uploading, setUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!assertCanWrite()) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `team-${Date.now()}.${fileExt}`;
      const filePath = `${orgId}/team/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('content-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('content-images')
        .getPublicUrl(filePath);

      onAvatarChange(publicUrl);
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div
      className="relative w-20 h-20 mx-auto mb-4 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => { if (assertCanWrite()) fileInputRef.current?.click(); }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        disabled={!canWrite}
        onChange={handleUpload}
        className="hidden"
      />
      
      {avatar ? (
        <img
          src={avatar}
          alt={name}
          className="w-20 h-20 rounded-full object-cover"
        />
      ) : (
        <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-2xl font-bold text-primary-foreground">
          {initials || getInitials(name)}
        </div>
      )}
      
      {/* Hover overlay */}
      {(isHovered || uploading) && (
        <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
          {uploading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload className="w-6 h-6 text-white" />
          )}
        </div>
      )}
    </div>
  );
}

interface AboutPageRendererProps {
  content?: Record<string, unknown> | null;
  onChange: (content: Record<string, unknown>) => void;
}

type AboutValueDraft = { icon: string; title: string; description: string };
type AboutTeamDraft = { name: string; role: string; bio: string; avatar?: string; initials?: string; sourceId?: string };
type AboutResultImageDraft = { url?: string; alt?: string };

export function AboutPageRenderer({ content, onChange }: AboutPageRendererProps) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { data: teamSource = [] } = useOrgTeamMembers(orgId);
  const syncedContent = mergeTeamMembersIntoAbout((content ?? null) as AboutContent | null, teamSource);
  const data = { ...DEFAULT_CONTENT, ...syncedContent };
  
  const hero = { ...DEFAULT_CONTENT.hero, ...data.hero };
  const valuesTitle = data.valuesTitle || DEFAULT_CONTENT.valuesTitle;
  const values = (data.values || DEFAULT_CONTENT.values) as AboutValueDraft[];
  const teamTitle = data.teamTitle || DEFAULT_CONTENT.teamTitle;
  const team = (data.team || DEFAULT_CONTENT.team) as AboutTeamDraft[];
  const results = { ...DEFAULT_CONTENT.results, ...data.results };
  const resultImages = (results.images?.length ? results.images : DEFAULT_CONTENT.results.images) as AboutResultImageDraft[];
  const resultFallbacks = [resultsLivingRoom, resultsKitchen, resultsBathroom];
  const cta = { ...DEFAULT_CONTENT.cta, ...data.cta };

  const update = (section: string, newData: unknown) => {
    onChange({ ...data, [section]: newData });
  };

  // Value management
  const addValue = () => {
    if (!assertCanWrite()) return;
    const newValues = [...values, { icon: 'Star', title: 'New Value', description: 'Description of this value' }];
    update('values', newValues);
  };

  const removeValue = (index: number) => {
    if (!assertCanWrite()) return;
    const newValues = values.filter((_value, i: number) => i !== index);
    update('values', newValues);
  };

  // Team management
  const addTeamMember = () => {
    if (!assertCanWrite()) return;
    const newTeam = [...team, { name: 'New Member', role: 'Role Title', bio: 'Bio description here...', avatar: '', initials: 'NM' }];
    update('team', newTeam);
  };

  const removeTeamMember = (index: number) => {
    if (!assertCanWrite()) return;
    const newTeam = team.filter((_member, i: number) => i !== index);
    update('team', newTeam);
  };

  // Feature management  
  const addFeature = () => {
    if (!assertCanWrite()) return;
    const features = [...(hero.features || []), 'New feature'];
    update('hero', { ...hero, features });
  };

  const removeFeature = (index: number) => {
    if (!assertCanWrite()) return;
    const features = hero.features.filter((_feature: string, i: number) => i !== index);
    update('hero', { ...hero, features });
  };

  return (
    <div className="min-h-full bg-background">
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <EditableText
                value={hero.title}
                onChange={(v) => update('hero', { ...hero, title: v })}
                as="h1"
                className="text-4xl md:text-5xl font-bold text-foreground mb-6"
              />
              <EditableText
                value={hero.subtitle}
                onChange={(v) => update('hero', { ...hero, subtitle: v })}
                as="p"
                className="text-lg text-muted-foreground mb-8 leading-relaxed"
              />
              <div className="flex flex-wrap gap-6 text-sm">
                {hero.features?.map((feature: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 group/feature">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <EditableText
                      value={feature}
                      onChange={(v) => {
                        const features = [...hero.features];
                        features[i] = v;
                        update('hero', { ...hero, features });
                      }}
                      className="text-foreground font-medium"
                    />
                    <button
                      disabled={!canWrite}
                      onClick={() => removeFeature(i)}
                      className="opacity-0 group-hover/feature:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </button>
                  </div>
                ))}
                <button
                  disabled={!canWrite}
                  onClick={addFeature}
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <EditableImage
                src={hero.imageUrl}
                alt="About hero"
                onChange={(url) => update('hero', { ...hero, imageUrl: url })}
                fallbackSrc={aboutHeroImage}
                aspectRatio="4/3"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <EditableText
              value={valuesTitle}
              onChange={(v) => update('valuesTitle', v)}
              as="h2"
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value: AboutValueDraft, i: number) => (
              <div key={i} className="text-center p-6 bg-card rounded-2xl border border-border group/value relative">
                <IconPicker
                  value={value.icon}
                  onChange={(icon) => {
                    const newValues = [...values];
                    newValues[i] = { ...newValues[i], icon };
                    update('values', newValues);
                  }}
                />
                <EditableText
                  value={value.title}
                  onChange={(v) => {
                    const newValues = [...values];
                    newValues[i] = { ...newValues[i], title: v };
                    update('values', newValues);
                  }}
                  as="h3"
                  className="font-semibold text-foreground mb-2 text-lg"
                />
                <EditableText
                  value={value.description}
                  onChange={(v) => {
                    const newValues = [...values];
                    newValues[i] = { ...newValues[i], description: v };
                    update('values', newValues);
                  }}
                  as="p"
                  className="text-muted-foreground text-sm leading-relaxed"
                />
                <button
                  disabled={!canWrite}
                  onClick={() => removeValue(i)}
                  className="absolute top-2 right-2 opacity-0 group-hover/value:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              </div>
            ))}
            
            {/* Add Value Button */}
            <button
              disabled={!canWrite}
              onClick={addValue}
              className="p-6 bg-card rounded-2xl border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-2"
            >
              <Plus className="w-8 h-8 text-muted-foreground" />
              <span className="text-muted-foreground font-medium">Add Value</span>
            </button>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <EditableText
              value={teamTitle}
              onChange={(v) => update('teamTitle', v)}
              as="h2"
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {team.map((member: AboutTeamDraft, i: number) => (
              <div key={i} className="bg-background rounded-2xl p-8 shadow-sm text-center group/member relative">
                <TeamAvatar
                  avatar={member.avatar}
                  initials={member.initials || member.name.split(' ').map((n: string) => n[0]).join('')}
                  name={member.name}
                  onAvatarChange={(url) => {
                    const newTeam = [...team];
                    newTeam[i] = { ...newTeam[i], avatar: url };
                    update('team', newTeam);
                  }}
                />
                <EditableText
                  value={member.name}
                  onChange={(v) => {
                    const newTeam = [...team];
                    newTeam[i] = { ...newTeam[i], name: v };
                    update('team', newTeam);
                  }}
                  as="h3"
                  className="font-semibold text-foreground text-lg mb-1"
                />
                <EditableText
                  value={member.role}
                  onChange={(v) => {
                    const newTeam = [...team];
                    newTeam[i] = { ...newTeam[i], role: v };
                    update('team', newTeam);
                  }}
                  as="p"
                  className="text-primary text-sm font-medium mb-4"
                />
                <EditableText
                  value={member.bio}
                  onChange={(v) => {
                    const newTeam = [...team];
                    newTeam[i] = { ...newTeam[i], bio: v };
                    update('team', newTeam);
                  }}
                  as="p"
                  className="text-muted-foreground text-sm leading-relaxed"
                />
                <button
                  disabled={!canWrite}
                  onClick={() => removeTeamMember(i)}
                  className="absolute top-2 right-2 opacity-0 group-hover/member:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              </div>
            ))}
            
            {/* Add Team Member Button */}
            <button
              disabled={!canWrite}
              onClick={addTeamMember}
              className="bg-background rounded-2xl p-8 shadow-sm border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-2"
            >
              <Plus className="w-8 h-8 text-muted-foreground" />
              <span className="text-muted-foreground font-medium">Add Team Member</span>
            </button>
          </div>
        </div>
      </section>

      {/* Results Gallery */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <EditableText
              value={results.title}
              onChange={(v) => update('results', { ...results, title: v })}
              as="h2"
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            />
            <EditableText
              value={results.subtitle || 'What a clean looks like'}
              onChange={(v) => update('results', { ...results, subtitle: v })}
              as="p"
              className="text-muted-foreground"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {resultImages.slice(0, 3).map((image: { url?: string; alt?: string }, i: number) => (
              <div key={i} className="space-y-3">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <EditableImage
                    src={image.url}
                    alt={image.alt || `Result image ${i + 1}`}
                    onChange={(url) => {
                      const images = [...resultImages];
                      images[i] = { ...images[i], url };
                      update('results', { ...results, images });
                    }}
                    fallbackSrc={resultFallbacks[i % resultFallbacks.length]}
                    aspectRatio="4/3"
                  />
                </div>
                <EditableText
                  value={image.alt || `Result image ${i + 1}`}
                  onChange={(alt) => {
                    const images = [...resultImages];
                    images[i] = { ...images[i], alt };
                    update('results', { ...results, images });
                  }}
                  className="text-xs text-muted-foreground block text-center"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <EditableText
            value={cta.title}
            onChange={(v) => update('cta', { ...cta, title: v })}
            as="h2"
            className="text-3xl font-bold text-primary-foreground mb-4"
          />
          <EditableText
            value={cta.subtitle}
            onChange={(v) => update('cta', { ...cta, subtitle: v })}
            as="p"
            className="text-primary-foreground/80 text-lg max-w-xl mx-auto mb-8"
          />
          <Button size="xl" variant="secondary" className="bg-card text-foreground pointer-events-none">
            <EditableText
              value={cta.buttonText}
              onChange={(v) => update('cta', { ...cta, buttonText: v })}
              className="inline"
            />
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
